import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import MobileBottomNav from "@/components/layout/MobileBottomNav";
import Home from "@/pages/Home";
import ProductListing from "@/pages/ProductListing";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import UserAccount from "@/pages/UserAccount";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminShipments from "@/pages/admin/AdminShipments";
import AdminPrescriptions from "@/pages/admin/AdminPrescriptions";
import AdminLabTests from "@/pages/admin/AdminLabTests";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminWallet from "@/pages/admin/AdminWallet";
import AdminContent from "@/pages/admin/AdminContent";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminProfile from "@/pages/admin/AdminProfile";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-sky-50"><div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-sky-50"><div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full" /></div>;
  if (!user) return <AdminLogin />;
  if (user.role !== "admin") return <Navigate to="/" />;
  return children;
}

function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const user = await login(email, password);
      if (user.role !== "admin") {
        setError("This account does not have admin access");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <img src="https://customer-assets.emergentagent.com/job_9d18eba3-f81c-4175-a241-09119da09ef9/artifacts/lx9rjtoy_WhatsApp%20Image%202026-03-01%20at%204.03.51%20PM.jpeg" alt="Logo" className="h-12 w-12 rounded-xl mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-800">Admin Panel</h1>
            <p className="text-sm text-slate-500">Sign in to Pharma Palace Dashboard</p>
          </div>
          {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-xl mb-4 text-center">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-600">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="admin@pharmapalace.com" className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" data-testid="admin-email-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Enter password" className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" data-testid="admin-password-input" />
            </div>
            <button type="submit" disabled={submitting} className="w-full py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-full font-semibold text-sm transition-colors disabled:opacity-50" data-testid="admin-login-btn">
              {submitting ? "Signing in..." : "Sign In to Dashboard"}
            </button>
          </form>
          <a href="/" className="block text-center text-xs text-slate-400 mt-4 hover:text-cyan-600">Back to Store</a>
        </div>
      </div>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Navbar />}
      {!isAdmin && <MobileBottomNav />}
      <main className={isAdmin ? "" : "min-h-screen"}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<ProductListing />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="/order-confirmation/:orderId" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
          <Route path="/login" element={<Auth />} />
          <Route path="/account/*" element={<ProtectedRoute><UserAccount /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="analytics" element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="inventory" element={<AdminInventory />} />
            <Route path="shipments" element={<AdminShipments />} />
            <Route path="prescriptions" element={<AdminPrescriptions />} />
            <Route path="lab-tests" element={<AdminLabTests />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="wallet" element={<AdminWallet />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="profile" element={<AdminProfile />} />
          </Route>
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
